'use client';

import { useState } from 'react';
import Link from 'next/link';
import { useCommandCenterTool } from '@/lib/command-center/useCommandCenterTool';

interface AssessmentSection {
  id: string;
  title: string;
  description: string;
  questions: Question[];
}

interface Question {
  id: string;
  text: string;
  helpText?: string;
  weight: number;
}

interface Answers {
  [key: string]: boolean | null;
}

const sections: AssessmentSection[] = [
  {
    id: 'lawful-basis',
    title: 'Lawful Basis for Processing',
    description: 'Article 6 - Legal grounds for processing personal data',
    questions: [
      { id: 'lb1', text: 'Have you identified a lawful basis for each processing activity?', weight: 10 },
      { id: 'lb2', text: 'Do you document the lawful basis for each processing operation?', weight: 8 },
      { id: 'lb3', text: 'Where you rely on consent, is it freely given, specific, informed, and unambiguous?', weight: 10 },
      { id: 'lb4', text: 'Do you provide clear mechanisms for withdrawing consent?', weight: 8 },
      { id: 'lb5', text: 'Have you assessed legitimate interests where applicable (LIA)?', weight: 7 },
    ],
  },
  {
    id: 'data-subject-rights',
    title: 'Data Subject Rights',
    description: 'Articles 12-23 - Rights of individuals',
    questions: [
      { id: 'dsr1', text: 'Can you respond to subject access requests within 30 days?', weight: 10 },
      { id: 'dsr2', text: 'Do you have processes for right to rectification requests?', weight: 8 },
      { id: 'dsr3', text: 'Can you facilitate the right to erasure ("right to be forgotten")?', weight: 9 },
      { id: 'dsr4', text: 'Do you support data portability where applicable?', weight: 7 },
      { id: 'dsr5', text: 'Can individuals object to processing of their data?', weight: 8 },
      { id: 'dsr6', text: 'Do you have mechanisms to restrict processing when requested?', weight: 7 },
    ],
  },
  {
    id: 'transparency',
    title: 'Transparency & Information',
    description: 'Articles 12-14 - Privacy notices and transparency',
    questions: [
      { id: 't1', text: 'Do you provide clear and accessible privacy notices?', weight: 9 },
      { id: 't2', text: 'Do privacy notices cover all required information (identity, purposes, recipients, etc.)?', weight: 8 },
      { id: 't3', text: 'Are privacy notices provided at the point of data collection?', weight: 8 },
      { id: 't4', text: 'Do you inform individuals about their rights?', weight: 7 },
      { id: 't5', text: 'Is information provided in clear, plain language?', weight: 7 },
    ],
  },
  {
    id: 'security',
    title: 'Data Security',
    description: 'Article 32 - Security of processing',
    questions: [
      { id: 's1', text: 'Do you implement encryption for personal data at rest and in transit?', weight: 10 },
      { id: 's2', text: 'Do you have access controls limiting who can access personal data?', weight: 9 },
      { id: 's3', text: 'Do you regularly test and evaluate security measures?', weight: 8 },
      { id: 's4', text: 'Do you have incident response procedures for data breaches?', weight: 9 },
      { id: 's5', text: 'Do you maintain confidentiality, integrity, and availability of systems?', weight: 8 },
      { id: 's6', text: 'Can you restore access to data in case of incidents?', weight: 7 },
    ],
  },
  {
    id: 'breach-notification',
    title: 'Breach Notification',
    description: 'Articles 33-34 - Data breach procedures',
    questions: [
      { id: 'bn1', text: 'Can you detect data breaches in a timely manner?', weight: 9 },
      { id: 'bn2', text: 'Do you have procedures to notify supervisory authorities within 72 hours?', weight: 10 },
      { id: 'bn3', text: 'Do you have procedures to notify affected individuals when required?', weight: 9 },
      { id: 'bn4', text: 'Do you document all breaches, including minor ones?', weight: 7 },
      { id: 'bn5', text: 'Do you assess the risk to individuals from breaches?', weight: 8 },
    ],
  },
  {
    id: 'dpia',
    title: 'Data Protection Impact Assessments',
    description: 'Article 35 - Risk assessment for high-risk processing',
    questions: [
      { id: 'd1', text: 'Do you conduct DPIAs for high-risk processing activities?', weight: 9 },
      { id: 'd2', text: 'Do DPIAs assess necessity, proportionality, and risks?', weight: 8 },
      { id: 'd3', text: 'Do you consult the supervisory authority when required?', weight: 7 },
      { id: 'd4', text: 'Do you document and review DPIAs regularly?', weight: 7 },
      { id: 'd5', text: 'Do you involve the DPO in DPIA processes?', weight: 6 },
    ],
  },
  {
    id: 'accountability',
    title: 'Accountability & Governance',
    description: 'Article 5(2) & Article 24 - Demonstrating compliance',
    questions: [
      { id: 'a1', text: 'Do you maintain records of processing activities (Article 30)?', weight: 10 },
      { id: 'a2', text: 'Have you appointed a Data Protection Officer where required?', weight: 8 },
      { id: 'a3', text: 'Do you have data protection policies and procedures?', weight: 8 },
      { id: 'a4', text: 'Do you provide data protection training to staff?', weight: 7 },
      { id: 'a5', text: 'Do you conduct regular compliance audits?', weight: 7 },
      { id: 'a6', text: 'Do you implement data protection by design and default?', weight: 8 },
    ],
  },
  {
    id: 'international-transfers',
    title: 'International Data Transfers',
    description: 'Chapter V - Transfers to third countries',
    questions: [
      { id: 'it1', text: 'Have you mapped international data transfers?', weight: 9 },
      { id: 'it2', text: 'Do you use appropriate transfer mechanisms (SCCs, adequacy decisions)?', weight: 10 },
      { id: 'it3', text: 'Have you conducted Transfer Impact Assessments where required?', weight: 8 },
      { id: 'it4', text: 'Do you document the legal basis for each transfer?', weight: 7 },
      { id: 'it5', text: 'Do you monitor changes in adequacy decisions and laws?', weight: 6 },
    ],
  },
  {
    id: 'processors',
    title: 'Third-Party Processors',
    description: 'Article 28 - Processor relationships',
    questions: [
      { id: 'p1', text: 'Do you have written contracts with all data processors?', weight: 10 },
      { id: 'p2', text: 'Do contracts include all Article 28 required provisions?', weight: 9 },
      { id: 'p3', text: 'Do you conduct due diligence on processors before engagement?', weight: 8 },
      { id: 'p4', text: 'Do you monitor processor compliance regularly?', weight: 7 },
      { id: 'p5', text: 'Are sub-processor arrangements properly authorized and documented?', weight: 7 },
    ],
  },
];

export default function GDPRCompliancePage() {
  const [view, setView] = useState<'landing' | 'assessment' | 'results'>('landing');
  const [currentSection, setCurrentSection] = useState(0);
  const [answers, setAnswers] = useState<Answers>({});
  const [saved, setSaved] = useState(false);

  // Command Center integration
  const {
    enabled: commandCenterEnabled,
    saving,
    error: saveError,
    saveResult,
  } = useCommandCenterTool({
    toolId: 'gdpr-compliance',
    toolCategory: 'compliance',
  });

  const handleAnswer = (questionId: string, value: boolean) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  };

  const calculateResults = () => {
    let totalWeight = 0;
    let earnedWeight = 0;

    const sectionScores = sections.map((section) => {
      let sectionTotal = 0;
      let sectionEarned = 0;

      section.questions.forEach((q) => {
        sectionTotal += q.weight;
        totalWeight += q.weight;
        if (answers[q.id] === true) {
          sectionEarned += q.weight;
          earnedWeight += q.weight;
        }
      });

      return {
        id: section.id,
        title: section.title,
        score: sectionTotal > 0 ? Math.round((sectionEarned / sectionTotal) * 100) : 0,
        gaps: section.questions.filter((q) => answers[q.id] !== true),
      };
    });

    return {
      overallScore: totalWeight > 0 ? Math.round((earnedWeight / totalWeight) * 100) : 0,
      sectionScores,
    };
  };

  const results = view === 'results' ? calculateResults() : null;

  // Save to Command Center
  const handleSaveToCommandCenter = async () => {
    if (!results || !commandCenterEnabled) return;

    const gaps = results.sectionScores.flatMap(section =>
      section.gaps.map(gap => ({
        id: gap.id,
        title: gap.text,
        severity: section.score < 40 ? 'critical' as const :
                  section.score < 60 ? 'high' as const :
                  section.score < 80 ? 'medium' as const : 'low' as const,
        description: `Compliance gap in ${section.title}`,
      }))
    );

    const recommendations = results.sectionScores
      .filter(s => s.score < 80)
      .sort((a, b) => a.score - b.score)
      .map(section => ({
        id: section.id,
        title: `Address ${section.title} gaps`,
        priority: section.score < 40 ? 'high' as const :
                  section.score < 60 ? 'high' as const : 'medium' as const,
        description: `Current score: ${section.score}%. ${section.gaps.length} gaps identified.`,
      }));

    const scores: Record<string, number> = {};
    results.sectionScores.forEach(s => {
      scores[s.id] = s.score;
    });

    const assessment = await saveResult({
      name: `GDPR Compliance - ${new Date().toLocaleDateString()}`,
      score_overall: results.overallScore,
      scores,
      answers,
      gaps,
      recommendations,
    });

    if (assessment) {
      setSaved(true);
    }
  };

  const exportReport = () => {
    if (!results) return;

    const lines = [
      '# GDPR Compliance Assessment Report',
      '',
      `**Date:** ${new Date().toLocaleDateString()}`,
      `**Overall Score:** ${results.overallScore}%`,
      '',
      '---',
      '',
      '## Section Scores',
      '',
      ...results.sectionScores.map((s) => `- **${s.title}:** ${s.score}%`),
      '',
      '## Compliance Gaps',
      '',
    ];

    results.sectionScores.forEach((section) => {
      if (section.gaps.length > 0) {
        lines.push(`### ${section.title}`);
        lines.push('');
        section.gaps.forEach((gap) => {
          lines.push(`- [ ] ${gap.text}`);
        });
        lines.push('');
      }
    });

    lines.push('---');
    lines.push('*Generated by The Art of CTO - GDPR Compliance Tool*');

    const blob = new Blob([lines.join('\n')], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `gdpr-assessment-${new Date().toISOString().split('T')[0]}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'text-green-400';
    if (score >= 60) return 'text-yellow-400';
    if (score >= 40) return 'text-accent';
    return 'text-red-400';
  };

  const getScoreLabel = (score: number) => {
    if (score >= 80) return 'Strong Compliance';
    if (score >= 60) return 'Moderate Compliance';
    if (score >= 40) return 'Needs Improvement';
    return 'Significant Gaps';
  };

  return (
    <div className="min-h-screen bg-white dark:bg-background py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Breadcrumb */}
        <nav className="mb-8 text-sm text-light-text-secondary dark:text-foreground-tertiary">
          <Link href="/tools" className="hover:text-accent transition-colors">Tools</Link>
          <span className="mx-2">/</span>
          <span className="text-light-text dark:text-foreground">GDPR Compliance</span>
        </nav>

        {view === 'landing' && (
          <>
            <div className="text-center mb-12">
              <div className="inline-block px-3 py-1 bg-blue-500/20 text-blue-400 rounded-full text-sm font-medium mb-4">
                EU Regulation 2016/679
              </div>
              <h1 className="text-4xl md:text-5xl font-bold text-light-text dark:text-foreground mb-4">
                GDPR Compliance Assessment
              </h1>
              <p className="text-xl text-light-text-secondary dark:text-foreground-secondary max-w-3xl mx-auto">
                Assess your organization&apos;s compliance with the General Data Protection Regulation
                and identify gaps in your data protection practices.
              </p>
            </div>

            {/* Key Areas */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
              <div className="bg-light-surface dark:bg-surface rounded-lg p-6 text-center">
                <div className="text-4xl mb-3">📋</div>
                <h3 className="font-semibold text-light-text dark:text-foreground mb-2">9 Key Areas</h3>
                <p className="text-sm text-light-text-secondary dark:text-foreground-secondary">
                  Covering all major GDPR requirements from lawful basis to international transfers
                </p>
              </div>
              <div className="bg-light-surface dark:bg-surface rounded-lg p-6 text-center">
                <div className="text-4xl mb-3">✅</div>
                <h3 className="font-semibold text-light-text dark:text-foreground mb-2">50+ Checks</h3>
                <p className="text-sm text-light-text-secondary dark:text-foreground-secondary">
                  Comprehensive questions based on GDPR articles and regulatory guidance
                </p>
              </div>
              <div className="bg-light-surface dark:bg-surface rounded-lg p-6 text-center">
                <div className="text-4xl mb-3">📊</div>
                <h3 className="font-semibold text-light-text dark:text-foreground mb-2">Gap Analysis</h3>
                <p className="text-sm text-light-text-secondary dark:text-foreground-secondary">
                  Get prioritized recommendations and exportable compliance report
                </p>
              </div>
            </div>

            {/* CTA */}
            <div className="text-center mb-12">
              <button
                onClick={() => setView('assessment')}
                className="px-8 py-4 bg-accent text-accent-foreground rounded-lg text-lg font-bold hover:bg-accent-hover transition-colors"
              >
                Start Assessment
              </button>
              <p className="text-sm text-light-text-secondary dark:text-foreground-tertiary mt-3">
                Takes approximately 10-15 minutes
              </p>
            </div>

            {/* GDPR Overview */}
            <div className="bg-light-surface dark:bg-surface rounded-lg p-8">
              <h2 className="text-2xl font-bold text-light-text dark:text-foreground mb-6">Key GDPR Requirements</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="font-semibold text-light-text dark:text-foreground mb-3">Data Protection Principles (Art. 5)</h3>
                  <ul className="text-sm text-light-text-secondary dark:text-foreground-secondary space-y-2">
                    <li>• Lawfulness, fairness, transparency</li>
                    <li>• Purpose limitation</li>
                    <li>• Data minimization</li>
                    <li>• Accuracy</li>
                    <li>• Storage limitation</li>
                    <li>• Integrity and confidentiality</li>
                  </ul>
                </div>
                <div>
                  <h3 className="font-semibold text-light-text dark:text-foreground mb-3">Potential Penalties</h3>
                  <ul className="text-sm text-light-text-secondary dark:text-foreground-secondary space-y-2">
                    <li>• Up to €20 million or 4% of global turnover</li>
                    <li>• Individual liability for officers</li>
                    <li>• Enforcement orders and bans</li>
                    <li>• Reputational damage</li>
                  </ul>
                </div>
              </div>
            </div>
          </>
        )}

        {view === 'assessment' && (
          <>
            {/* Progress */}
            <div className="mb-8">
              <div className="flex justify-between text-sm text-light-text-secondary dark:text-foreground-tertiary mb-2">
                <span>Section {currentSection + 1} of {sections.length}</span>
                <span>{Math.round(((currentSection + 1) / sections.length) * 100)}%</span>
              </div>
              <div className="h-2 bg-light-border dark:bg-surface rounded-full overflow-hidden">
                <div
                  className="h-full bg-accent transition-all"
                  style={{ width: `${((currentSection + 1) / sections.length) * 100}%` }}
                />
              </div>
            </div>

            {/* Section */}
            <div className="bg-light-surface dark:bg-surface rounded-lg p-8 mb-8">
              <h2 className="text-2xl font-bold text-light-text dark:text-foreground mb-2">
                {sections[currentSection].title}
              </h2>
              <p className="text-light-text-secondary dark:text-foreground-secondary mb-8">
                {sections[currentSection].description}
              </p>

              <div className="space-y-6">
                {sections[currentSection].questions.map((question) => (
                  <div key={question.id} className="border-b border-light-border dark:border-border-muted pb-6">
                    <p className="text-light-text dark:text-foreground mb-3">{question.text}</p>
                    {question.helpText && (
                      <p className="text-sm text-light-text-secondary dark:text-foreground-tertiary mb-3">{question.helpText}</p>
                    )}
                    <div className="flex gap-4">
                      <button
                        onClick={() => handleAnswer(question.id, true)}
                        className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                          answers[question.id] === true
                            ? 'bg-green-500 text-white'
                            : 'bg-light-border dark:bg-background text-light-text dark:text-foreground hover:bg-green-500/20'
                        }`}
                      >
                        Yes
                      </button>
                      <button
                        onClick={() => handleAnswer(question.id, false)}
                        className={`px-6 py-2 rounded-lg font-medium transition-colors ${
                          answers[question.id] === false
                            ? 'bg-red-500 text-white'
                            : 'bg-light-border dark:bg-background text-light-text dark:text-foreground hover:bg-red-500/20'
                        }`}
                      >
                        No
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Navigation */}
            <div className="flex justify-between">
              <button
                onClick={() => currentSection > 0 ? setCurrentSection(currentSection - 1) : setView('landing')}
                className="px-6 py-3 border border-light-border dark:border-border rounded-lg text-light-text dark:text-foreground hover:border-accent transition-colors"
              >
                {currentSection > 0 ? '← Previous' : '← Back'}
              </button>
              <button
                onClick={() => {
                  if (currentSection < sections.length - 1) {
                    setCurrentSection(currentSection + 1);
                  } else {
                    setView('results');
                  }
                }}
                className="px-6 py-3 bg-accent text-accent-foreground rounded-lg font-medium hover:bg-accent-hover transition-colors"
              >
                {currentSection < sections.length - 1 ? 'Next Section →' : 'View Results →'}
              </button>
            </div>
          </>
        )}

        {view === 'results' && results && (
          <>
            <div className="text-center mb-12">
              <h1 className="text-4xl font-bold text-light-text dark:text-foreground mb-4">Assessment Results</h1>
              <div className={`text-6xl font-bold mb-2 ${getScoreColor(results.overallScore)}`}>
                {results.overallScore}%
              </div>
              <p className={`text-xl ${getScoreColor(results.overallScore)}`}>
                {getScoreLabel(results.overallScore)}
              </p>
            </div>

            {/* Section Breakdown */}
            <div className="bg-light-surface dark:bg-surface rounded-lg p-8 mb-8">
              <h2 className="text-2xl font-bold text-light-text dark:text-foreground mb-6">Compliance by Area</h2>
              <div className="space-y-4">
                {results.sectionScores.map((section) => (
                  <div key={section.id}>
                    <div className="flex justify-between mb-2">
                      <span className="text-light-text dark:text-foreground">{section.title}</span>
                      <span className={getScoreColor(section.score)}>{section.score}%</span>
                    </div>
                    <div className="h-3 bg-light-border dark:bg-background rounded-full overflow-hidden">
                      <div
                        className={`h-full transition-all ${
                          section.score >= 80 ? 'bg-green-500' :
                          section.score >= 60 ? 'bg-yellow-500' :
                          section.score >= 40 ? 'bg-accent' : 'bg-red-500'
                        }`}
                        style={{ width: `${section.score}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Priority Gaps */}
            <div className="bg-light-surface dark:bg-surface rounded-lg p-8 mb-8">
              <h2 className="text-2xl font-bold text-light-text dark:text-foreground mb-6">Priority Actions</h2>
              {results.sectionScores
                .filter((s) => s.gaps.length > 0)
                .sort((a, b) => a.score - b.score)
                .slice(0, 3)
                .map((section) => (
                  <div key={section.id} className="mb-6 last:mb-0">
                    <h3 className="font-semibold text-accent mb-3">{section.title}</h3>
                    <ul className="space-y-2">
                      {section.gaps.slice(0, 3).map((gap) => (
                        <li key={gap.id} className="flex items-start gap-2 text-sm text-light-text-secondary dark:text-foreground-secondary">
                          <span className="text-red-400 mt-0.5">○</span>
                          {gap.text}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
            </div>

            {/* Actions */}
            <div className="flex flex-wrap gap-4 justify-center">
              <button
                onClick={exportReport}
                className="px-6 py-3 bg-accent text-accent-foreground rounded-lg font-medium hover:bg-accent-hover transition-colors"
              >
                Export Report
              </button>
              {commandCenterEnabled && !saved && (
                <button
                  onClick={handleSaveToCommandCenter}
                  disabled={saving}
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-500 transition-colors disabled:opacity-50"
                >
                  {saving ? 'Saving...' : 'Save to Command Center'}
                </button>
              )}
              {saved && (
                <div className="px-6 py-3 bg-green-600/20 text-green-400 rounded-lg font-medium flex items-center gap-2">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Saved
                </div>
              )}
              <button
                onClick={() => {
                  setView('landing');
                  setCurrentSection(0);
                  setAnswers({});
                  setSaved(false);
                }}
                className="px-6 py-3 border border-light-border dark:border-border rounded-lg text-light-text dark:text-foreground hover:border-accent transition-colors"
              >
                Start Over
              </button>
              <Link
                href="/tools"
                className="px-6 py-3 border border-light-border dark:border-border rounded-lg text-light-text dark:text-foreground hover:border-accent transition-colors"
              >
                Back to Tools
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
